import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Snackbar } from '@mui/material';
import {
  loadTeamInbox,
  loadTeamConversation,
  loadOlderTeamMessages,
  refreshTeamMessages,
  sendTeamMessage,
  sendTeamMessageWithAttachment,
  markTeamRead,
  createTeamDirect,
  createTeamGroup,
  loadTeamGroupMembers,
  addTeamGroupMembers,
  leaveTeamGroup,
  removeTeamGroupMember,
  loadCompanyMembers,
  setTeamConversation,
  setTeamMessages,
  upsertTeamMessage,
  upsertTeamInboxItem,
  applyTeamReadReceipt,
} from '../../Redux/ActionCreators/chatAction';
import './TeamChat.scss';

// ─── Avatar helpers ───────────────────────────────────────────────────────────
const AVATAR_COLORS = {
  A: '#F75891', B: '#4A90D9', C: '#7B68EE', D: '#FF6B6B',
  E: '#00BCD4', F: '#F4A261', G: '#2D9E6B', H: '#E76F51',
  I: '#8338EC', J: '#3A86FF', K: '#06D6A0', L: '#E6A817',
  M: '#E63946', N: '#4361EE', O: '#F77F00', P: '#9C27B0',
  Q: '#0077B6', R: '#D62828', S: '#0288D1', T: '#558B2F',
  U: '#AD1457', V: '#1B6B45', W: '#6D6875', X: '#B5179E',
  Y: '#7209B7', Z: '#560BAD',
};
const getAvatarColor = (name) => {
  if (!name) return '#8a9199';
  return AVATAR_COLORS[name.trim().charAt(0).toUpperCase()] || '#8a9199';
};
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

// ─── Icons ────────────────────────────────────────────────────────────────────
const SendIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);
const AttachIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const UsersIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

// ─── File type icon ───────────────────────────────────────────────────────────
const resolveFileType = (name = '', mimeType = '') => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf' || mimeType.includes('pdf')) return { color: '#991b1b', bg: '#fee2e2', label: 'PDF' };
  if (['xlsx', 'xls', 'csv'].includes(ext)) return { color: '#15803d', bg: '#dcfce7', label: 'XLS' };
  if (['doc', 'docx'].includes(ext)) return { color: '#1e40af', bg: '#dbeafe', label: 'DOC' };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext) || mimeType.includes('image')) return { color: '#92400e', bg: '#fef3c7', label: 'IMG' };
  return { color: '#6b7280', bg: '#f3f4f6', label: ext.toUpperCase().slice(0, 4) || 'FILE' };
};
const FileTypeIcon = ({ name, mimeType }) => {
  const { color, bg, label } = resolveFileType(name, mimeType);
  return (
    <svg width="36" height="42" viewBox="0 0 36 42" fill="none">
      <path d="M4 0h20l12 12v26a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z" fill={bg} />
      <path d="M24 0l12 12H28a4 4 0 0 1-4-4V0z" fill={color} fillOpacity="0.35" />
      <text x="18" y="30" textAnchor="middle" fill={color} fontSize="8.5" fontWeight="800" fontFamily="Arial, sans-serif">{label}</text>
    </svg>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatTime = (d) => d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
const formatInboxTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};
const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const handleDownload = async (url, fileName) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch { window.open(url, '_blank'); }
};
const getDateLabel = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};
const isHtmlEmpty = (html) => !html || html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').trim() === '';
const parseCurrentUserId = () => {
  try {
    const t = localStorage.getItem('token') || '';
    const p = JSON.parse(atob(t.split('.')[1]));
    const raw = p.user_id ?? p.UserId ?? p.userId;
    return raw != null ? Number(raw) : null;
  } catch { return null; }
};
const formatTcLastActive = (lastActiveAt, isOnline) => {
  if (isOnline) return 'Active now';
  if (!lastActiveAt) return 'Offline';
  const now = new Date();
  const lastActive = new Date(lastActiveAt);
  const diffMin = Math.floor((now - lastActive) / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin <= 10) return `${diffMin} min ago`;
  const day = lastActive.getDate();
  const month = lastActive.toLocaleString('en-US', { month: 'short' });
  const year = lastActive.getFullYear();
  const time = lastActive.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
  return `${day} ${month} ${year}, ${time}`;
};

const convTitle = (conv) => {
  if (!conv) return '';
  if (conv.conversation_type === 'direct') return conv.other_user_name || conv.other_user_email || 'Direct message';
  return conv.title || 'Group chat';
};

const FILE_MESSAGE_PREFIX = '📎 ';
const looksLikeFileMsg = (msg) => (msg?.body_text || '').trim().startsWith(FILE_MESSAGE_PREFIX);
const shouldShowTeamMsgBody = (msg) => {
  const body = (msg.body_text || '').trim();
  if (!body) return false;
  const atts = msg.attachments || [];
  if (!atts.length) return !looksLikeFileMsg(msg);
  if (atts.length === 1) {
    const fileName = (atts[0].file_name || '').trim();
    if (body === FILE_MESSAGE_PREFIX + fileName || body === fileName) return false;
  }
  return true;
};

// ─── New Group Modal ──────────────────────────────────────────────────────────
const NewGroupModal = ({ onClose, onCreated }) => {
  const dispatch = useDispatch();
  const [title, setTitle] = useState('');
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    dispatch(loadCompanyMembers()).then((res) => {
      const currentId = parseCurrentUserId();
      const rows = (res.data || [])
        .map((m) => ({ user_id: Number(m.user_id), name: (m.name || '').trim() || null, email: (m.email || '').trim() || null, avatar: m.avatar || null }))
        .filter((u) => u.user_id && u.user_id !== currentId);
      setMembers(rows);
      setLoading(false);
    });
  }, [dispatch]);

  const filtered = members.filter((u) => {
    const q = search.toLowerCase();
    return !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const toggle = (uid) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(uid) ? next.delete(uid) : next.add(uid);
    return next;
  });

  const handleCreate = async () => {
    if (!title.trim() || selected.size === 0 || creating) return;
    setCreating(true);
    const res = await dispatch(createTeamGroup(title.trim(), Array.from(selected)));
    setCreating(false);
    if (res.success) onCreated(res.data);
    else onClose();
  };

  return (
    <div className="tc-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tc-modal">
        <div className="tc-modal-head">
          <div>
            <div className="tc-modal-title">New Team Chat</div>
            <div className="tc-modal-subtitle">Create a group chat for a subset of your workspace members.</div>
          </div>
          <button className="tc-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="tc-modal-body">
          <label className="tc-modal-label">Team Name</label>
          <input
            className="tc-modal-input"
            placeholder="e.g. Design Team, Sprint 4…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <label className="tc-modal-label" style={{ marginTop: 16 }}>
            Add Members <span className="tc-modal-count">({selected.size} selected)</span>
          </label>
          <div className="tc-modal-search-wrap">
            <SearchIcon />
            <input
              className="tc-modal-search"
              placeholder="Search members…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="tc-modal-member-list">
            {loading ? (
              <div className="tc-modal-loading"><div className="spinner-border spinner-border-sm" role="status" /></div>
            ) : filtered.length === 0 ? (
              <p className="tc-modal-empty">No members found</p>
            ) : filtered.map((u) => {
              const checked = selected.has(u.user_id);
              return (
                <label key={u.user_id} className={`tc-modal-member${checked ? ' checked' : ''}`} onClick={() => toggle(u.user_id)}>
                  <div className="tc-modal-member-avatar" style={{ background: getAvatarColor(u.name || u.email) }}>
                    {u.avatar ? <img src={u.avatar} alt="" /> : getInitials(u.name || u.email || '?')}
                  </div>
                  <div className="tc-modal-member-info">
                    <div className="tc-modal-member-name">{u.name || `User ${u.user_id}`}</div>
                    <div className="tc-modal-member-email">{u.email || ''}</div>
                  </div>
                  <div className={`tc-modal-checkbox${checked ? ' checked' : ''}`}>
                    {checked && <svg width="10" height="10" viewBox="0 0 10 10"><polyline points="1.5,5 4,7.5 8.5,2.5" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        <div className="tc-modal-foot">
          <button className="tc-modal-btn-cancel" onClick={onClose}>Cancel</button>
          <button
            className="tc-modal-btn-create"
            disabled={!title.trim() || selected.size === 0 || creating}
            onClick={handleCreate}
          >
            {creating ? 'Creating…' : 'Create Team Chat'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── New DM Modal ─────────────────────────────────────────────────────────────
const NewDmModal = ({ onClose, onCreated }) => {
  const dispatch = useDispatch();
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    dispatch(loadCompanyMembers()).then((res) => {
      const currentId = parseCurrentUserId();
      const rows = (res.data || [])
        .map((m) => ({ user_id: Number(m.user_id), name: (m.name || '').trim() || null, email: (m.email || '').trim() || null, avatar: m.avatar || null }))
        .filter((u) => u.user_id && u.user_id !== currentId);
      setMembers(rows);
      setLoading(false);
    });
  }, [dispatch]);

  const filtered = members.filter((u) => {
    const q = search.toLowerCase();
    return !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const handleStart = async () => {
    if (!selected || creating) return;
    setCreating(true);
    const res = await dispatch(createTeamDirect(selected));
    setCreating(false);
    if (res.success) onCreated(res.data);
    else onClose();
  };

  return (
    <div className="tc-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tc-modal">
        <div className="tc-modal-head">
          <div>
            <div className="tc-modal-title">New Direct Message</div>
            <div className="tc-modal-subtitle">Send a private message to a workspace member.</div>
          </div>
          <button className="tc-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="tc-modal-body">
          <div className="tc-modal-search-wrap">
            <SearchIcon />
            <input className="tc-modal-search" placeholder="Search members…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="tc-modal-member-list">
            {loading ? (
              <div className="tc-modal-loading"><div className="spinner-border spinner-border-sm" role="status" /></div>
            ) : filtered.length === 0 ? (
              <p className="tc-modal-empty">No members found</p>
            ) : filtered.map((u) => {
              const checked = selected === u.user_id;
              return (
                <label key={u.user_id} className={`tc-modal-member${checked ? ' checked' : ''}`} onClick={() => setSelected(u.user_id)}>
                  <div className="tc-modal-member-avatar" style={{ background: getAvatarColor(u.name || u.email) }}>
                    {u.avatar ? <img src={u.avatar} alt="" /> : getInitials(u.name || u.email || '?')}
                  </div>
                  <div className="tc-modal-member-info">
                    <div className="tc-modal-member-name">{u.name || `User ${u.user_id}`}</div>
                    <div className="tc-modal-member-email">{u.email || ''}</div>
                  </div>
                  <div className={`tc-modal-checkbox tc-modal-checkbox--radio${checked ? ' checked' : ''}`}>
                    {checked && <div className="tc-modal-radio-dot" />}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        <div className="tc-modal-foot">
          <button className="tc-modal-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="tc-modal-btn-create" disabled={!selected || creating} onClick={handleStart}>
            {creating ? 'Starting…' : 'Start Chat'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Leave Confirm Modal ──────────────────────────────────────────────────────
const LeaveConfirmModal = ({ groupName, onCancel, onConfirm, leaving }) => (
  <div className="tc-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onCancel()}>
    <div className="tc-modal tc-modal--confirm">
      <div className="tc-confirm-icon">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>
      <div className="tc-confirm-body">
        <div className="tc-confirm-title">Leave group?</div>
        <div className="tc-confirm-desc">
          You'll leave <strong>{groupName}</strong> and will no longer receive messages from this group.
        </div>
      </div>
      <div className="tc-confirm-foot">
        <button className="tc-modal-btn-cancel" onClick={onCancel} disabled={leaving}>
          Cancel
        </button>
        <button className="tc-confirm-btn-leave" onClick={onConfirm} disabled={leaving}>
          {leaving ? 'Leaving…' : 'Leave group'}
        </button>
      </div>
    </div>
  </div>
);

// ─── Members Modal ────────────────────────────────────────────────────────────
const MembersModal = ({ convId, canAddMembers, canRemoveMembers, onClose }) => {
  const dispatch = useDispatch();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addMode, setAddMode] = useState(false);
  const [allMembers, setAllMembers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const currentId = parseCurrentUserId();
  const memberIds = new Set(members.map((m) => Number(m.user_id)));

  const reloadMembers = () =>
    dispatch(loadTeamGroupMembers(convId)).then((res) => {
      setMembers(res.data || []);
    });

  useEffect(() => {
    dispatch(loadTeamGroupMembers(convId)).then((res) => {
      setMembers(res.data || []);
      setLoading(false);
    });
  }, [dispatch, convId]);

  const openAddMode = async () => {
    setAddMode(true);
    const res = await dispatch(loadCompanyMembers());
    const rows = (res.data || [])
      .map((m) => ({ user_id: Number(m.user_id), name: (m.name || '').trim() || null, email: (m.email || '').trim() || null }))
      .filter((u) => u.user_id && u.user_id !== currentId && !memberIds.has(u.user_id));
    setAllMembers(rows);
  };

  const filteredAdd = allMembers.filter((u) => {
    const q = search.toLowerCase();
    return !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const toggle = (uid) => setSelected((prev) => { const n = new Set(prev); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });

  const handleAdd = async () => {
    if (!selected.size || adding) return;
    setAdding(true);
    await dispatch(addTeamGroupMembers(convId, Array.from(selected)));
    setAdding(false);
    await reloadMembers();
    setAddMode(false);
    setSelected(new Set());
  };

  const handleRemove = async (userId) => {
    if (removingId) return;
    setRemovingId(userId);
    await dispatch(removeTeamGroupMember(convId, userId));
    setRemovingId(null);
    await reloadMembers();
  };

  return (
    <div className="tc-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tc-modal">
        <div className="tc-modal-head">
          <div className="tc-modal-title">Group Members</div>
          <button className="tc-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="tc-modal-body">
          {loading ? <div className="tc-modal-loading"><div className="spinner-border spinner-border-sm" role="status" /></div>
          : !addMode ? (
            <div className="tc-members-list">
              {members.map((m) => {
                const uid = Number(m.user_id);
                const isSelf = uid === currentId;
                const isOwner = m.role === 'owner';
                const showRemove = canRemoveMembers && !isSelf && !isOwner;
                return (
                  <div key={m.user_id} className="tc-members-row">
                    <div className="tc-modal-member-avatar" style={{ background: getAvatarColor(m.name || m.email) }}>
                      {getInitials(m.name || m.email || '?')}
                    </div>
                    <div className="tc-modal-member-info">
                      <div className="tc-modal-member-name">{m.name || `User ${m.user_id}`}</div>
                      <div className="tc-modal-member-email">{m.email || ''}</div>
                    </div>
                    <span className="tc-member-role">{m.role}</span>
                    {showRemove && (
                      <button
                        className="tc-remove-member-btn"
                        disabled={removingId === uid}
                        onClick={() => handleRemove(uid)}
                        title="Remove from group"
                      >
                        {removingId === uid ? '…' : 'Remove'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <div className="tc-modal-search-wrap">
                <SearchIcon />
                <input className="tc-modal-search" placeholder="Search members…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className="tc-modal-member-list">
                {filteredAdd.length === 0 ? <p className="tc-modal-empty">No members to add</p>
                : filteredAdd.map((u) => {
                  const checked = selected.has(u.user_id);
                  return (
                    <label key={u.user_id} className={`tc-modal-member${checked ? ' checked' : ''}`} onClick={() => toggle(u.user_id)}>
                      <div className="tc-modal-member-avatar" style={{ background: getAvatarColor(u.name || u.email) }}>
                        {getInitials(u.name || u.email || '?')}
                      </div>
                      <div className="tc-modal-member-info">
                        <div className="tc-modal-member-name">{u.name || `User ${u.user_id}`}</div>
                        <div className="tc-modal-member-email">{u.email || ''}</div>
                      </div>
                      <div className={`tc-modal-checkbox${checked ? ' checked' : ''}`}>
                        {checked && <svg width="10" height="10" viewBox="0 0 10 10"><polyline points="1.5,5 4,7.5 8.5,2.5" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="tc-modal-foot">
          {!addMode ? (
            <>
              {canAddMembers && <button className="tc-modal-btn-cancel" onClick={openAddMode}>Add Members</button>}
              <button className="tc-modal-btn-create" onClick={onClose}>Done</button>
            </>
          ) : (
            <>
              <button className="tc-modal-btn-cancel" onClick={() => { setAddMode(false); setSelected(new Set()); }}>Cancel</button>
              <button className="tc-modal-btn-create" disabled={!selected.size || adding} onClick={handleAdd}>
                {adding ? 'Adding…' : 'Add Selected'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const TeamChat = ({ wsRef: parentWsRef }) => {
  const dispatch = useDispatch();
  const teamInbox = useSelector((s) => s.chat.teamInbox);
  const teamConversation = useSelector((s) => s.chat.teamConversation);
  const teamMessages = useSelector((s) => s.chat.teamMessages);

  const [activeConvId, setActiveConvId] = useState(null);
  const [convLoading, setConvLoading] = useState(false);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [messageBody, setMessageBody] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [showNewDm, setShowNewDm] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [mobileView, setMobileView] = useState('inbox');

  const currentUserId = useRef(parseCurrentUserId());
  const activeConvIdRef = useRef(null);
  const teamConversationRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatEditorRef = useRef(null);
  const messageBodyRef = useRef('');
  const isFetchingOlderRef = useRef(false);
  const lastMarkedReadRef = useRef(null);
  const attachmentSyncInFlightRef = useRef(false);

  useEffect(() => { activeConvIdRef.current = activeConvId; }, [activeConvId]);
  useEffect(() => { teamConversationRef.current = teamConversation; }, [teamConversation]);

  // ── Load inbox on mount ───────────────────────────────────────────────────
  useEffect(() => {
    dispatch(loadTeamInbox()).then(() => setInboxLoading(false));
  }, [dispatch]);

  // ── Reset editor when conversation changes ────────────────────────────────
  useEffect(() => {
    setMessageBody('');
    messageBodyRef.current = '';
    if (chatEditorRef.current) chatEditorRef.current.innerHTML = '';
    setPendingFile(null);
    setHasMoreMessages(true);
    lastMarkedReadRef.current = null;
    isFetchingOlderRef.current = false;
  }, [activeConvId]);

  // ── Auto-scroll on new messages ───────────────────────────────────────────
  useEffect(() => {
    if (!teamMessages.length) return;
    const el = messagesContainerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollTop >= scrollHeight - clientHeight - 80) {
      el.scrollTo({ top: scrollHeight, behavior: 'smooth' });
    }
  }, [teamMessages]);

  // ── Auto mark read ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeConvId || !teamMessages.length) return;
    const uid = currentUserId.current;
    const newest = [...teamMessages].reverse().find((m) => m.message_type === 'user' && Number(m.sender_user_id) !== uid);
    if (newest && newest.id !== lastMarkedReadRef.current) {
      lastMarkedReadRef.current = newest.id;
      dispatch(markTeamRead(activeConvId, newest.id)).then(() => {
        dispatch(upsertTeamInboxItem({ conversation_id: activeConvId, unread_count: 0 }));
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamMessages, activeConvId]);

  // ── Re-fetch messages when a WS file message arrives without attachment URLs ─
  useEffect(() => {
    const needsSync = teamMessages.some(
      (m) => m.message_type === 'user' && looksLikeFileMsg(m) && !(m.attachments || []).length
    );
    if (!needsSync || attachmentSyncInFlightRef.current || !activeConvId) return;
    attachmentSyncInFlightRef.current = true;
    dispatch(refreshTeamMessages(activeConvId))
      .finally(() => { attachmentSyncInFlightRef.current = false; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamMessages, activeConvId]);

  // ── WS event handler (passed down from MessengerPage via parentWsRef) ─────
  const handleWsEvent = useCallback((data) => {
    if (data.type === 'internal_inbox_updated' && data.item) {
      dispatch(upsertTeamInboxItem(data.item));
      if (
        teamConversationRef.current?.conversation_type === 'direct' &&
        String(data.item.conversation_id) === String(teamConversationRef.current?.conversation_id) &&
        data.item.other_user_online != null
      ) {
        dispatch(setTeamConversation({
          ...teamConversationRef.current,
          other_user_online: !!data.item.other_user_online,
        }));
      }
      return;
    }
    if (data.type === 'internal_message' && data.message) {
      const isActive = String(data.conversation_id) === String(teamConversationRef.current?.conversation_id);
      if (isActive) {
        dispatch(upsertTeamMessage(data.message));
        const uid = currentUserId.current;
        if (Number(data.message.sender_user_id) !== uid) {
          dispatch(markTeamRead(activeConvIdRef.current, data.message.id));
        }
      }
      return;
    }
    if (data.type === 'internal_message_read' && data.conversation_id) {
      if (String(data.conversation_id) === String(teamConversationRef.current?.conversation_id)) {
        dispatch(applyTeamReadReceipt(data.user_id, data.last_read_message_id, data.last_read_at));
      }
      return;
    }
    if (data.type === 'internal_membership_updated') {
      if (String(data.conversation_id) === String(teamConversationRef.current?.conversation_id)) {
        dispatch(loadTeamConversation(activeConvIdRef.current));
      }
    }
  }, [dispatch]);

  // Register handler with parent WS
  useEffect(() => {
    if (parentWsRef) parentWsRef.teamHandler = handleWsEvent;
    return () => { if (parentWsRef) parentWsRef.teamHandler = null; };
  }, [handleWsEvent, parentWsRef]);

  const selectConversation = async (convId) => {
    if (convId === activeConvIdRef.current) return;
    setActiveConvId(convId);
    setMobileView('chat');
    dispatch(setTeamConversation(null));
    dispatch(setTeamMessages([]));
    setConvLoading(true);
    setHasMoreMessages(true);
    const result = await dispatch(loadTeamConversation(convId));
    setConvLoading(false);
    if (result.success) {
      dispatch(upsertTeamInboxItem({ conversation_id: convId, unread_count: 0 }));
      if (parentWsRef?.joinTeamGroup) parentWsRef.joinTeamGroup(convId);
    }
  };

  const fetchOlderMessages = async () => {
    if (!hasMoreMessages || isFetchingOlderRef.current || !teamMessages.length) return;
    isFetchingOlderRef.current = true;
    setLoadingOlder(true);
    const oldest = teamMessages[0];
    const res = await dispatch(loadOlderTeamMessages(activeConvId, oldest.id));
    if (res?.hasMore === false) setHasMoreMessages(false);
    setLoadingOlder(false);
    isFetchingOlderRef.current = false;
  };

  const handleScroll = () => {
    const el = messagesContainerRef.current;
    if (!el || isFetchingOlderRef.current) return;
    if (el.scrollTop <= 50) fetchOlderMessages();
  };

  const handleSend = async () => {
    const html = messageBodyRef.current;
    if ((isHtmlEmpty(html) && !pendingFile) || sending) return;
    setSending(true);
    messageBodyRef.current = '';
    setMessageBody('');
    if (chatEditorRef.current) chatEditorRef.current.innerHTML = '';

    if (pendingFile) {
      const f = pendingFile; setPendingFile(null);
      const res = await dispatch(sendTeamMessageWithAttachment(activeConvId, f.file, isHtmlEmpty(html) ? undefined : html));
      if (!res.success) { setSnackMessage('Failed to send attachment.'); setSnackOpen(true); }
    } else {
      const res = await dispatch(sendTeamMessage(activeConvId, html));
      if (!res.success) { setSnackMessage('Failed to send message.'); setSnackOpen(true); }
    }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile({ file, name: file.name, size: file.size });
    e.target.value = '';
  };

  const handleLeave = () => setShowLeaveConfirm(true);

  const confirmLeave = async () => {
    setLeaving(true);
    const res = await dispatch(leaveTeamGroup(activeConvId));
    setLeaving(false);
    setShowLeaveConfirm(false);
    if (res.success) {
      setActiveConvId(null);
      dispatch(setTeamConversation(null));
      dispatch(setTeamMessages([]));
      setMobileView('inbox');
      dispatch(loadTeamInbox());
    }
  };

  const handleConvCreated = async (data) => {
    setShowNewGroup(false);
    setShowNewDm(false);
    if (!data?.conversation_id) return;
    // Load inbox first (SET_TEAM_INBOX replaces the whole array)
    await dispatch(loadTeamInbox());
    // Then upsert the new conversation on top — this guarantees the correct
    // conversation_type / other_user_name are present even if the backend
    // hasn't indexed the new conversation in the listing yet
    dispatch(upsertTeamInboxItem({
      conversation_id: data.conversation_id,
      conversation_type: data.conversation_type,
      other_user_name: data.other_user_name,
      other_user_email: data.other_user_email,
      title: data.title,
      member_count: data.member_count,
      last_message_at: data.last_message_at || new Date().toISOString(),
      unread_count: 0,
    }));
    await selectConversation(String(data.conversation_id));
  };

  // ── Build grouped message list ────────────────────────────────────────────
  const groupedItems = [];
  let currentDay = null;
  teamMessages.forEach((msg) => {
    const day = msg.created_at ? new Date(msg.created_at).toDateString() : '';
    if (day !== currentDay) {
      currentDay = day;
      groupedItems.push({ type: 'date', label: getDateLabel(msg.created_at) });
    }
    groupedItems.push({ type: 'msg', msg });
  });

  const isGroup = teamConversation?.conversation_type === 'group';
  const title = convTitle(teamConversation);
  const uid = currentUserId.current;

  return (
    <div className="tc-layout">
      {/* ── Inbox sidebar ── */}
      <aside className={`tc-sidebar${mobileView === 'inbox' ? ' mobile-open' : ''}`}>
        <div className="tc-sidebar-header">
          <span className="tc-sidebar-title">Team</span>
          <div className="tc-sidebar-actions">
            <button className="tc-action-btn" title="New Direct Message" onClick={() => setShowNewDm(true)}>
              <PlusIcon /><span>DM</span>
            </button>
            <button className="tc-action-btn" title="New Group" onClick={() => setShowNewGroup(true)}>
              <UsersIcon /><span>Group</span>
            </button>
          </div>
        </div>
        <div className="tc-inbox-list">
          {inboxLoading ? (
            <div className="tc-inbox-loading"><div className="spinner-border spinner-border-sm" role="status" /></div>
          ) : teamInbox.length === 0 ? (
            <p className="tc-inbox-empty">No conversations yet.<br />Start a DM or group chat.</p>
          ) : teamInbox.map((item) => {
            const key = String(item.conversation_id);
            const isActive = key === activeConvId;
            const name = item.conversation_type === 'direct'
              ? (item.other_user_name || item.other_user_email || 'Direct message')
              : (item.title || 'Group chat');
            const typeLabel = item.conversation_type === 'group' ? `Group · ${item.member_count || ''} members` : 'Direct';
            return (
              <div key={key} className={`tc-inbox-item${isActive ? ' active' : ''}`} onClick={() => selectConversation(key)}>
                <div className="tc-inbox-avatar" style={{ background: getAvatarColor(name) }}>
                  {getInitials(name)}
                </div>
                <div className="tc-inbox-body">
                  <div className="tc-inbox-row-top">
                    <span className="tc-inbox-name">
                      {name}
                    </span>
                    <span className="tc-inbox-time">{formatInboxTime(item.last_message_at)}</span>
                  </div>
                  <div className="tc-inbox-sub">
                    <span className="tc-inbox-type">{typeLabel}</span>
                    {item.unread_count > 0 && (
                      <span className="tc-unread-pill">{item.unread_count > 99 ? '99+' : item.unread_count}</span>
                    )}
                  </div>
                  {/* {item.last_message_preview && (
                    <div className="tc-inbox-preview">{item.last_message_preview}</div>
                  )} */}
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      {/* ── Chat main ── */}
      <main className={`tc-main${mobileView === 'chat' ? ' mobile-chat' : ''}`}>
        {activeConvId && (
          <button className="tc-mobile-back" onClick={() => setMobileView('inbox')}>
            <span>&#8592;</span> Team
          </button>
        )}

        {!activeConvId ? (
          <div className="tc-empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <p>Select a conversation or start a new one</p>
          </div>
        ) : convLoading ? (
          <div className="tc-loading"><div className="spinner-border" role="status" style={{ width: '1.5rem', height: '1.5rem' }} /></div>
        ) : (
          <div className="tc-window">
            {/* Header */}
            <div className="tc-header">
              <div className="tc-header-avatar" style={{ background: getAvatarColor(title) }}>
                {getInitials(title)}
              </div>
              <div className="tc-header-info">
                <div className="tc-header-name">{title}</div>
                {!isGroup && (
                  <div className="tc-status">
                    <span className={`tc-dot${teamConversation.other_user_online ? ' online' : ''}`} />
                    {formatTcLastActive(teamConversation.other_user_last_active_at, teamConversation.other_user_online)}
                  </div>
                )}
                {isGroup && teamConversation && (
                  <div className="tc-header-sub">{teamConversation.member_count || 0} members</div>
                )}
              </div>
              <div className="tc-header-actions">
                {isGroup && (
                  <button className="tc-header-btn" onClick={() => setShowMembers(true)}>
                    <UsersIcon /> Members
                  </button>
                )}
                {isGroup && teamConversation?.can_leave && teamConversation?.my_role !== 'member' && (
                  <button className="tc-header-btn tc-header-btn--leave" onClick={handleLeave}>
                    Leave
                  </button>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="tc-messages" ref={messagesContainerRef} onScroll={handleScroll}>
              {!hasMoreMessages && teamMessages.length > 0 && (
                <div className="tc-conversation-start">Beginning of conversation</div>
              )}
              {loadingOlder && (
                <div className="tc-older-loading"><div className="spinner-border spinner-border-sm" role="status" /></div>
              )}
              {teamMessages.length === 0 && (
                <p className="tc-empty-msg">No messages yet. Start the conversation.</p>
              )}
              {groupedItems.map((item, idx) => {
                if (item.type === 'date') {
                  return (
                    <div key={`d-${idx}`} className="tc-date-sep"><span>{item.label}</span></div>
                  );
                }
                const { msg } = item;
                const isOut = msg.message_type === 'user' && Number(msg.sender_user_id) === uid;
                const isSystem = msg.message_type === 'system';
                if (isSystem) {
                  return <div key={msg.id} className="tc-system-msg">{msg.body_text}</div>;
                }
                const senderName = msg.sender_name || (isOut ? 'You' : 'Member');
                const hasAttachments = (msg.attachments || []).length > 0;
                const showBody = shouldShowTeamMsgBody(msg);
                return (
                  <div key={msg.id} className={`tc-msg-row${isOut ? ' out' : ' in'}`}>
                    {!isOut && (
                      <div className="tc-msg-avatar" style={{ background: getAvatarColor(senderName) }} title={senderName}>
                        {getInitials(senderName)}
                      </div>
                    )}
                    <div className={`tc-message${isOut ? ' out' : ' in'}`}>
                      <div className={`tc-bubble${hasAttachments && !showBody ? ' tc-bubble--file' : ''}`}>
                        {showBody && (
                          <span dangerouslySetInnerHTML={{ __html: msg.body_text }} />
                        )}
                        {(msg.attachments || []).map((att, i) => {
                          const fileName = att.file_name || att.name || 'File';
                          const mimeType = att.mime_type || att.content_type || '';
                          const fileSize = att.file_size ?? att.size ?? null;
                          return (
                            <div key={i} className="tc-file-card" onClick={() => att.file_url && window.open(att.file_url, '_blank')}>
                              <div className="tc-file-card__icon"><FileTypeIcon name={fileName} mimeType={mimeType} /></div>
                              <div className="tc-file-card__body">
                                <div className="tc-file-card__name">{fileName}</div>
                                {fileSize != null && <div className="tc-file-card__size">{formatFileSize(fileSize)}</div>}
                              </div>
                              <button className="tc-file-card__dl" title="Download" onClick={(e) => { e.stopPropagation(); handleDownload(att.file_url, fileName); }}>
                                <DownloadIcon />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <div className="tc-msg-meta">
                        {!isOut && <span className="tc-msg-sender">{senderName}</span>}
                        <span>{formatTime(msg.created_at)}</span>
                        {isOut && (
                          <span title={msg.is_read_by_others ? 'Read' : 'Delivered'}>
                            {msg.is_read_by_others ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                    {isOut && (
                      <div className="tc-msg-avatar" style={{ background: getAvatarColor(senderName) }}>
                        {getInitials(senderName)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Composer */}
            <div className="tc-composer">
              {pendingFile && (
                <div className="tc-attach-preview">
                  <div className="tc-attach-preview-icon">
                    <FileTypeIcon name={pendingFile.name} mimeType={pendingFile.file?.type || ''} />
                  </div>
                  <div className="tc-attach-preview-body">
                    <div className="tc-attach-preview-name">{pendingFile.name}</div>
                    <div className="tc-attach-preview-meta">{formatFileSize(pendingFile.size)} · ready to send</div>
                  </div>
                  <button className="tc-attach-preview-clear" onClick={() => setPendingFile(null)}>×</button>
                </div>
              )}
              <div className="tc-composer-row">
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.gif" onChange={handleFileChange} />
                <button className="tc-attach-btn" onClick={() => fileInputRef.current?.click()} title="Attach file"><AttachIcon /></button>
                <div className="tc-rich-input-wrap">
                  <div
                    ref={chatEditorRef}
                    contentEditable={!pendingFile}
                    suppressContentEditableWarning
                    className="tc-rich-input"
                    data-placeholder={pendingFile ? 'Remove file to type…' : 'Message…'}
                    onInput={(e) => { const h = e.currentTarget.innerHTML; messageBodyRef.current = h; setMessageBody(h); }}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <button
                  className="tc-send-btn"
                  onClick={handleSend}
                  disabled={(isHtmlEmpty(messageBody) && !pendingFile) || sending}
                  title="Send"
                >
                  <SendIcon />
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {showNewGroup && <NewGroupModal onClose={() => setShowNewGroup(false)} onCreated={handleConvCreated} />}
      {showNewDm && <NewDmModal onClose={() => setShowNewDm(false)} onCreated={handleConvCreated} />}
      {showMembers && activeConvId && (
        <MembersModal
          convId={activeConvId}
          canAddMembers={!!teamConversation?.can_add_members}
          canRemoveMembers={!!teamConversation?.can_remove_members}
          onClose={() => setShowMembers(false)}
        />
      )}
      {showLeaveConfirm && (
        <LeaveConfirmModal
          groupName={convTitle(teamConversation)}
          leaving={leaving}
          onCancel={() => setShowLeaveConfirm(false)}
          onConfirm={confirmLeave}
        />
      )}

      <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)}>
        <Alert onClose={() => setSnackOpen(false)} severity="error" sx={{ width: '100%' }}>{snackMessage}</Alert>
      </Snackbar>
    </div>
  );
};

export default TeamChat;
